import {
    fetchServiceAccounts,
    createServiceAccount,
    updateServiceAccountKeypair,
    deleteServiceAccount
} from "@/api";
import i18n from "@/i18n";
import router from "@/router/index";
import { DEVELOPER_PROJECT_DETAIL } from "@/router/path";

export const state = {
    serviceAccounts: [],
    loadingServiceAccounts: false
};

export const getters = {
    serviceAccounts: ({ serviceAccounts }) => serviceAccounts,
    serviceAccount:
        ({ serviceAccounts }) =>
            accountId =>
                serviceAccounts.find(item => {
                    const itemAccountId = item.account.split("@")[0] ?? "";
                    return itemAccountId === accountId;
                }),
    serviceAccountsTableRow: ({ serviceAccounts }) =>
        serviceAccounts.map(item => {
            const row = ["account", "clientId", "customer", "scope"].map(
                key => {
                    const value = item[key];
                    const cell = { text: value || "-" };
                    switch (key) {
                    case "customer":
                        cell.text = value?.name || "-";
                        break;
                    case "scope":
                        if (value?.length > 0 && item.customer) {
                            cell.text = i18n.t("serviceAccounts.scope")[
                                value[0]
                            ];
                            if (value.length > 1) {
                                cell.text += i18n.t(
                                    "serviceAccounts.scopePostfix",
                                    [value.length - 1]
                                );
                            }
                        } else {
                            cell.text = "-";
                        }
                        break;
                    }
                    return cell;
                }
            );
            row.accountId = item.account.split("@")[0] ?? "";
            return row;
        })
};

export const actions = {
    async FETCH_SERVICE_ACCOUNTS({ commit, getters: { projectId } }) {
        try {
            commit("SET_LOADING_SERVICE_ACCOUNTS", true);
            const { data } = await fetchServiceAccounts(projectId);
            commit("SET_SERVICE_ACCOUNTS", data);
        } catch (e) {
            // 사용자에게 오류 노출하지 않음
        } finally {
            commit("SET_LOADING_SERVICE_ACCOUNTS", false);
        }
    },
    async CREATE_SERVICE_ACCOUNT({ dispatch, getters: { projectId } }, params) {
        try {
            await createServiceAccount(projectId, params);
        } catch (e) {
            dispatch("UPDATE_ERROR_SNACKBAR", e);
            return Promise.reject(e);
        }
    },
    DIALOG_CREATE_SERVICE_ACCOUNT({ dispatch }) {
        dispatch("UPDATE_DIALOG", {
            title: i18n.t("serviceAccounts.create.title"),
            slot: () =>
                import(
                    "@/components/dialogs/serviceAccounts/Dialog.createServiceAccount.vue"
                ),
            actions: [
                {
                    async_action: "confirm",
                    label: i18n.t("label.confirm")
                }
            ],
            confirm: async dialog => {
                dialog.close();
            }
        });
    },
    UPDATE_SERVICE_ACCOUNT_KEYPAIR(
        { dispatch, commit, getters: { projectId } },
        serviceAccountId
    ) {
        dispatch("UPDATE_DIALOG", {
            title: i18n.t("serviceAccounts.privateKey.title"),
            slot: () =>
                import(
                    "@/components/dialogs/serviceAccounts/Dialog.publishPrivateKey.vue"
                ),
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                {
                    async_action: "confirm",
                    label: i18n.t("label.confirm")
                }
            ],
            confirm: async dialog => {
                try {
                    await updateServiceAccountKeypair(
                        projectId,
                        serviceAccountId
                    );
                    commit("SET_SERVICE_ACCOUNT_IS_ISSUED", serviceAccountId);
                } catch (e) {
                    dispatch("UPDATE_ERROR_SNACKBAR", e);
                }
                dialog.close();
            }
        });
    },
    DELETE_SERVICE_ACCOUNT(
        { dispatch, getters: { projectId } },
        serviceAccountId
    ) {
        dispatch("UPDATE_DIALOG", {
            title: i18n.t("serviceAccounts.delete.title"),
            slot: () =>
                import(
                    "@/components/dialogs/serviceAccounts/Dialog.deleteServiceAccount.vue"
                ),
            confirmed: false,
            actions: [
                { action: "cancel", label: i18n.t("label.cancel") },
                {
                    async_action: "confirm",
                    label: i18n.t("label.remove"),
                    color: "danger",
                    disabled: true
                }
            ],
            confirm: async dialog => {
                try {
                    await deleteServiceAccount(projectId, serviceAccountId);
                    router.replace(
                        `${DEVELOPER_PROJECT_DETAIL}/${projectId}/service-accounts`
                    );
                } catch (e) {
                    dispatch("UPDATE_ERROR_SNACKBAR", e);
                }
                dialog.close();
            }
        });
    }
};

export const mutations = {
    SET_SERVICE_ACCOUNTS(state, value) {
        state.serviceAccounts = value;
    },
    SET_LOADING_SERVICE_ACCOUNTS(state, value) {
        state.loadingServiceAccounts = value;
    },
    SET_SERVICE_ACCOUNT_IS_ISSUED(state, serviceAccountId) {
        state.serviceAccounts = state.serviceAccounts.map(account => {
            if (account.id === serviceAccountId) {
                account.isIssued = true;
            }
            return account;
        });
    }
};
